import React from 'react';

// Simple chart components for basic visualizations
const AreaChart = ({ data, loading = false, className = '', ...props }) => {

  if (loading) {
    return (
      <div className={`chart area-chart loading ${className}`} {...props}>
        <div className="chart-skeleton"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`chart area-chart empty ${className}`} {...props}>
        <p>No chart data available</p>
      </div>
    );
  }

  // Simple SVG area chart implementation
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;
  
  const width = 100; // Use percentage for responsive width
  const height = 200;
  const padding = 40; // Increased padding for axis labels
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (100 - 2 * padding) + padding;
    const y = height - padding - ((d.value - minValue) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');
  
  const areaPath = `M ${padding},${height - padding} L ${points} L ${100 - padding},${height - padding} Z`;

  // Generate Y-axis labels
  const yAxisLabels = [];
  const numLabels = 5;
  for (let i = 0; i <= numLabels; i++) {
    const value = minValue + (range * i) / numLabels;
    const y = height - padding - (i / numLabels) * (height - 2 * padding);
    yAxisLabels.push({ value, y });
  }

  // Format numbers for display
  const formatValue = (value) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    } else {
      return value.toFixed(0);
    }
  };

  // Generate X-axis labels (time labels)
  const xAxisLabels = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (100 - 2 * padding) + padding;
    let label = '';
    
    // Format date labels
    if (d.date) {
      const date = new Date(d.date);
      label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (d.timestamp) {
      const date = new Date(d.timestamp);
      label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // For volume history data, show day numbers
      label = `Day ${i + 1}`;
    }
    
    return { label, x };
  }).filter((_, i) => i % Math.ceil(data.length / 6) === 0); // Show max 6 labels

  return (
    <div className={`chart area-chart ${className}`} {...props}>
      <svg width="100%" height={height} viewBox="0 0 100 200" preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(224, 15, 168, 0.3)" />
            <stop offset="100%" stopColor="rgba(221, 3, 83, 0.1)" />
          </linearGradient>
        </defs>
        
        {/* Y-axis grid lines */}
        {yAxisLabels.map((label, i) => (
          <line
            key={i}
            x1={padding}
            y1={label.y}
            x2={100 - padding}
            y2={label.y}
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth="0.5"
          />
        ))}
        
        {/* Y-axis labels */}
        {yAxisLabels.map((label, i) => (
          <text
            key={i}
            x={padding - 5}
            y={label.y + 2}
            textAnchor="end"
            fontSize="8"
            fill="rgba(255, 255, 255, 0.7)"
            fontFamily="var(--font-pixel-secondary)"
          >
            {formatValue(label.value)}
          </text>
        ))}
        
        {/* X-axis labels */}
        {xAxisLabels.map((label, i) => (
          <text
            key={i}
            x={label.x}
            y={height - padding + 15}
            textAnchor="middle"
            fontSize="8"
            fill="rgba(255, 255, 255, 0.7)"
            fontFamily="var(--font-pixel-secondary)"
          >
            {label.label}
          </text>
        ))}
        
        {/* Area chart */}
        <path
          d={areaPath}
          fill="url(#areaGradient)"
          stroke="#e00fa8"
          strokeWidth="0.5"
        />
        
        {/* Data points */}
        {data.map((d, i) => {
          const x = (i / (data.length - 1)) * (100 - 2 * padding) + padding;
          const y = height - padding - ((d.value - minValue) / range) * (height - 2 * padding);
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="1.5"
              fill="#e00fa8"
              className="chart-point"
            />
          );
        })}
      </svg>
    </div>
  );
};

const BarChart = ({ data, loading = false, className = '', ...props }) => {
  if (loading) {
    return (
      <div className={`chart bar-chart loading ${className}`} {...props}>
        <div className="chart-skeleton"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`chart bar-chart empty ${className}`} {...props}>
        <p>No chart data available</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value));
  const width = 400;
  const height = 200;
  const padding = 20;
  const barWidth = (width - 2 * padding) / data.length - 5;

  return (
    <div className={`chart bar-chart ${className}`} {...props}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {data.map((d, i) => {
          const barHeight = (d.value / maxValue) * (height - 2 * padding);
          const x = i * (barWidth + 5) + padding;
          const y = height - padding - barHeight;
          
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="#e00fa8"
              className="chart-bar"
            />
          );
        })}
      </svg>
    </div>
  );
};

const PieChart = ({ data, loading = false, className = '', ...props }) => {
  if (loading) {
    return (
      <div className={`chart pie-chart loading ${className}`} {...props}>
        <div className="chart-skeleton"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`chart pie-chart empty ${className}`} {...props}>
        <p>No chart data available</p>
      </div>
    );
  }

  const total = data.reduce((sum, d) => sum + d.value, 0);
  const colors = ['#e00fa8', '#dd0353', '#ff6b9d', '#ff8fab', '#ffb3c1'];
  
  let currentAngle = 0;
  const radius = 80;
  const centerX = 100;
  const centerY = 100;

  return (
    <div className={`chart pie-chart ${className}`} {...props}>
      <svg width={200} height={200} viewBox="0 0 200 200">
        {data.map((d, i) => {
          const percentage = d.value / total;
          const angle = percentage * 360;
          const endAngle = currentAngle + angle;
          
          const startAngleRad = (currentAngle * Math.PI) / 180;
          const endAngleRad = (endAngle * Math.PI) / 180;
          
          const x1 = centerX + radius * Math.cos(startAngleRad);
          const y1 = centerY + radius * Math.sin(startAngleRad);
          const x2 = centerX + radius * Math.cos(endAngleRad);
          const y2 = centerY + radius * Math.sin(endAngleRad);
          
          const largeArcFlag = angle > 180 ? 1 : 0;
          
          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');
          
          currentAngle = endAngle;
          
          return (
            <path
              key={i}
              d={pathData}
              fill={colors[i % colors.length]}
              className="chart-slice"
            />
          );
        })}
      </svg>
    </div>
  );
};

export { AreaChart, BarChart, PieChart };
